//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { Dispatch, SetStateAction, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

function init(initIndex: number) {
  return { activeIndex: initIndex };
}

function reducer(
  state: {
    activeIndex: number;
  },
  activeIndex: number
) {
  return { activeIndex };
}

const TabGroup = ({
  isHorizontal = false,
  otherNode,
  tabsClass = 'tabs overflow-hidden cyfi-tab',
  tabClass = 'tab',
  initIndex = 0,
  labels,
  childrens,
  navigateUrl,
  otherClass,
  setFocusTab,
  showCurrentTab = true,
  onClickPreFunction,
}: {
  isHorizontal?: boolean;
  otherNode?: React.ReactNode;
  tabsClass?: string;
  tabClass?: string;
  initIndex?: number;
  labels: string[] | React.ReactNode[];
  childrens: React.ReactNode[];
  navigateUrl?: string[];
  otherClass?: string;
  setFocusTab?: Dispatch<SetStateAction<number>>;
  showCurrentTab?: boolean;
  onClickPreFunction?: (tab: number, action: () => void) => Promise<boolean>;
}) => {
  const [state, dispatch] = useReducer(reducer, initIndex, init);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(initIndex);
    setFocusTab && setFocusTab(initIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initIndex]);

  return (
    <div className={isHorizontal ? 'flex space-x-4' : 'flex flex-col w-full h-full'}>
      <div className={(isHorizontal ? 'space-y-2' : 'flex w-full px-5 items-center relative justify-end') + (otherClass ? ` ${otherClass}` : '')}>
        {otherNode}
        <div className={tabsClass}>
          {labels.map((label, index) => (
            <div
              className={tabClass + (index === state.activeIndex ? ' tab-active' : '')}
              onClick={async () => {
                if (index === state.activeIndex) return; // 如果點擊的 tab 已經是當前 tab，則不需要執行任何操作
                const action = () => {
                  navigateUrl && navigate(navigateUrl[index]);
                  dispatch(index);
                  setFocusTab && setFocusTab(index);
                };
                if (onClickPreFunction) {
                  const result = await onClickPreFunction(index, action);
                  if (result === false) {
                    return;
                  }
                }
                action();
              }}
              key={index}
              data-testid={`click-tab-group-idx-${index}${navigateUrl ? '-link-to-' + navigateUrl[index] : ''}`}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
      {showCurrentTab === true ? (
        <div className={isHorizontal ? 'flex-1' : 'overflow-y-auto h-full'}>{childrens.find((children, index) => index === state.activeIndex)}</div>
      ) : (
        <div className={isHorizontal ? 'flex-1' : 'overflow-y-auto h-full max-h-[720px] webkit'}>
          {childrens.map((children, index) => (
            <div key={index} style={{ display: index === state.activeIndex ? 'block' : 'none' }}>
              {children}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TabGroup;
